import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { CHAINS, getExplorerAddressUrl, getExplorerTxUrl, getErrorMessage, adjustSignatureForSafe } from '../../services/api';
import { Icons } from './DashboardLayout';
import { ethers } from 'ethers';

// Status badge colors
const STATUS_COLORS = {
  PROPOSED: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
  CONFIRMED: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  EXECUTED: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
  FAILED: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  REJECTED: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  CANCELLED: { bg: 'bg-white/5', text: 'text-white/40', border: 'border-white/10' },
};

export default function MultiSigPage() {
  const { agents, selectedAgent, setSelectedAgent, token, refreshAgents } = useAuth();
  const [safeInfo, setSafeInfo] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [rejectedTxs, setRejectedTxs] = useState([]);
  const [allTxs, setAllTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [showManageSignersModal, setShowManageSignersModal] = useState(false);
  const [showChangeThresholdModal, setShowChangeThresholdModal] = useState(false);
  const [activeTab, setActiveTab] = useState('queue'); // 'queue' | 'rejected' | 'history'

  // Load Safe data when agent changes
  useEffect(() => {
    if (selectedAgent?.id && token) {
      loadSafeData();
    } else {
      setLoading(false);
    }
  }, [selectedAgent?.id, token]);

  const loadSafeData = async () => {
    if (!selectedAgent?.id) {
      console.warn('No agent selected, skipping Safe data load');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    console.log('Loading Safe data for agent:', selectedAgent.id);
    
    try {
      // Refresh agents first to get updated wallet data
      await refreshAgents();
      
      // Get wallets
      const walletsRes = await api.getAgentWallets(token, selectedAgent.id);
      console.log('Wallets response:', walletsRes);
      if (walletsRes.success && walletsRes.data) {
        // Handle both array and object with wallets property
        const walletsData = Array.isArray(walletsRes.data) 
          ? walletsRes.data 
          : (walletsRes.data.wallets || []);
        setWallets(walletsData);
      } else {
        setWallets([]);
      }

      // Get Safe info
      const safeRes = await api.getSafeInfo(token, selectedAgent.id);
      console.log('Safe info RAW response:', JSON.stringify(safeRes, null, 2));
      
      if (safeRes.success && safeRes.data) {
        // Normalize the response - handle ALL possible formats:
        // 1. { safes: [...] }
        // 2. { safe: {...} }
        // 3. Direct safe object with address
        // 4. Array of safes directly
        let normalizedSafeInfo = { safes: [] };
        const data = safeRes.data;
        
        if (Array.isArray(data)) {
          // Response is array directly: [{ address, threshold, ... }]
          normalizedSafeInfo = { safes: data };
        } else if (data.safes && Array.isArray(data.safes)) {
          // Response has safes array: { safes: [...] }
          normalizedSafeInfo = data;
        } else if (data.safe && data.safe.address) {
          // Response has single safe object: { safe: {...} }
          normalizedSafeInfo = { safes: [data.safe] };
        } else if (data.address) {
          // Response is direct safe object: { address, threshold, signers }
          normalizedSafeInfo = { safes: [data] };
        }
        
        console.log('Normalized Safe info:', normalizedSafeInfo);
        console.log('Has safes:', normalizedSafeInfo.safes.length > 0);
        setSafeInfo(normalizedSafeInfo);
      } else {
        console.log('No Safe found or error:', safeRes.error);
        setSafeInfo({ safes: [] });
      }

      // Get pending transactions (v8 endpoint)
      const pendingRes = await api.getSafePendingTxs(token, selectedAgent.id);
      console.log('Pending transactions response:', pendingRes);
      if (pendingRes.success && pendingRes.data?.transactions) {
        setPendingTxs(pendingRes.data.transactions);
      } else {
        setPendingTxs([]);
      }

      // Get executed transactions for history
      const allRes = await api.getSafeTransactions(token, selectedAgent.id, 'EXECUTED', 50);
      if (allRes.success && allRes.data?.transactions) {
        setAllTxs(allRes.data.transactions);
      } else {
        setAllTxs([]);
      }

      // Get rejected transactions
      const rejectedRes = await api.getSafeTransactions(token, selectedAgent.id, 'REJECTED', 50);
      if (rejectedRes.success && rejectedRes.data?.transactions) {
        setRejectedTxs(rejectedRes.data.transactions);
      } else {
        setRejectedTxs([]);
      }
    } catch (err) {
      console.error('Failed to load Safe data:', err);
      setSafeInfo(null);
    }
    setLoading(false);
  };

  // Determine current wallet type
  const primaryWallet = wallets.find(w => w.isDefault) || wallets[0];
  const isSafeActive = primaryWallet?.type === 'GNOSIS_SAFE';
  
  // Check for Safe existence - handle multiple response formats
  const safesArray = safeInfo?.safes || [];
  const hasSafe = Array.isArray(safesArray) && safesArray.length > 0;
  const safe = hasSafe ? safesArray[0] : null;
  
  console.log('Safe render state:', { 
    hasSafe, 
    safeCount: safesArray.length,
    safeAddress: safe?.address,
    safeInfo 
  });
  
  const smartAccountWallet = wallets.find(w => w.type === 'SMART_ACCOUNT');
  const safeWallet = wallets.find(w => w.type === 'GNOSIS_SAFE');
  
  // Get turnkey address from any wallet
  const turnkeyAddress = primaryWallet?.turnkeyAddress || wallets[0]?.turnkeyAddress;

  // Switch wallet handler
  const handleSwitchWallet = async (walletId) => {
    try {
      const res = await api.switchPrimaryWallet(token, selectedAgent.id, walletId);
      if (res.success) {
        loadSafeData();
      } else {
        alert(res.error?.message || 'Failed to switch wallet');
      }
    } catch (err) {
      alert('Failed to switch wallet');
    }
  };

  // No agents state
  if (agents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
          <Icons.Safe size={32} className="text-white/30" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Create an Agent First</h3>
        <p className="text-white/50 text-center max-w-md">
          Multi-sig wallets are configured per agent. Create an agent to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with agent selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white">Multi-Sig / Gnosis Safe</h1>
          <p className="text-white/50 text-sm mt-1">
            Manage multi-signature wallets for enhanced security
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Link to Safe Signing Page */}
          <a
            href="/dashboard/safesign"
            className="px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 font-medium text-sm hover:bg-green-500/20 transition-colors"
            data-testid="safesign-link"
          >
            <span className="flex items-center gap-2">
              <Icons.Shield size={16} />
              Sign Transactions
            </span>
          </a>
          <select
            value={selectedAgent?.id || ''}
            onChange={(e) => {
              const agent = agents.find(a => a.id === e.target.value);
              setSelectedAgent(agent);
            }}
            className="px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none focus:border-white/10 min-w-[180px]"
            data-testid="multisig-agent-selector"
          >
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 rounded-xl bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Current Wallet Status Banner */}
          <div className={`p-4 rounded-xl border ${isSafeActive ? 'bg-purple-500/5 border-purple-500/20' : 'bg-blue-500/5 border-blue-500/20'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                <span className="text-white font-medium">
                  Active: {isSafeActive ? `Gnosis Safe (${safe?.threshold || 2}-of-${safe?.signers?.length || 3})` : 'SimpleAccount'}
                </span>
              </div>
              {hasSafe && (
                <button
                  onClick={() => handleSwitchWallet(isSafeActive ? smartAccountWallet?.id : safeWallet?.id)}
                  className="px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white/70 text-sm hover:bg-white/[0.08] transition-colors"
                >
                  Switch to {isSafeActive ? 'SimpleAccount' : 'Gnosis Safe'}
                </button>
              )}
            </div>
            <p className="text-white/40 text-sm mt-2">
              {isSafeActive 
                ? 'All plugin executions require multi-sig approval before execution.'
                : 'Transactions execute immediately without approval.'}
            </p>
          </div>

          {/* Deploy Safe Section (if no Safe exists) */}
          {!hasSafe && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white">Setup Gnosis Safe</h2>
                  <p className="text-white/50 text-sm mt-1">Add multi-signature security to your agent</p>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={() => setShowDiscoverModal(true)}
                    className="px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white font-medium text-sm hover:bg-white/[0.08] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="discover-safes-btn"
                  >
                    <span className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                      Discover
                    </span>
                  </motion.button>
                  <motion.button
                    onClick={() => setShowImportModal(true)}
                    className="px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white font-medium text-sm hover:bg-white/[0.08] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="import-safe-btn"
                  >
                    <span className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Import
                    </span>
                  </motion.button>
                  <motion.button
                    onClick={() => setShowDeployModal(true)}
                    className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="deploy-safe-btn"
                  >
                    Deploy New
                  </motion.button>
                </div>
              </div>
              
              {/* Agent Signer Address - Required for gas */}
              {turnkeyAddress && (
                <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-yellow-400 text-sm font-medium">Agent Signer (EOA)</span>
                    <span className="text-yellow-400/60 text-xs">Pays gas fees</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-white font-mono text-sm flex-1 truncate">{turnkeyAddress}</code>
                    <button 
                      onClick={() => navigator.clipboard.writeText(turnkeyAddress)}
                      className="px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-400 text-xs font-medium hover:bg-yellow-500/20 transition-colors"
                      data-testid="copy-signer-address"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-yellow-400/60 text-xs mt-2">
                    This address needs ETH on Robinhood Network to pay gas for Safe deployment and transactions.
                  </p>
                </div>
              )}
              
              <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
                <p className="text-blue-400/80 text-sm">
                  A Gnosis Safe is an on-chain multi-signature wallet. Once deployed, you can require multiple approvals 
                  for any transaction your agent executes. Recommended for high-value agents.
                </p>
              </div>
            </div>
          )}

          {/* Safe Info Section (if Safe exists) */}
          {hasSafe && safe && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Safe Configuration</h2>
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={() => setShowManageSignersModal(true)}
                    className="px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white/70 text-xs hover:bg-white/[0.08] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="manage-signers-btn"
                  >
                    Manage Signers
                  </motion.button>
                  <motion.button
                    onClick={() => setShowChangeThresholdModal(true)}
                    className="px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white/70 text-xs hover:bg-white/[0.08] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="change-threshold-btn"
                  >
                    Change Threshold
                  </motion.button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Address Card */}
                <div className="p-4 rounded-xl bg-black/30">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Safe Address</p>
                  <div className="flex items-center gap-2">
                    <code className="text-white font-mono text-sm flex-1 truncate">{safe.address}</code>
                    <button 
                      onClick={() => navigator.clipboard.writeText(safe.address)}
                      className="p-1.5 rounded-lg hover:bg-white/[0.05] text-white/40 hover:text-white/70"
                    >
                      <Icons.Copy size={14} />
                    </button>
                    <a 
                      href={getExplorerAddressUrl(safe.chain || 'base', safe.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg hover:bg-white/[0.05] text-white/40 hover:text-white/70"
                    >
                      <Icons.External size={14} />
                    </a>
                  </div>
                </div>

                {/* Threshold Card */}
                <div className="p-4 rounded-xl bg-black/30">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Threshold</p>
                  <p className="text-white text-xl font-bold">
                    {safe.threshold} of {safe.signers?.length} 
                    <span className="text-white/40 text-sm font-normal ml-2">signers required</span>
                  </p>
                </div>
              </div>

              {/* Signers List */}
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Signers ({safe.signers?.length})</p>
                <div className="space-y-2">
                  {safe.signers?.map((signer, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-black/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-medium">
                          {i + 1}
                        </div>
                        <code className="text-white/70 font-mono text-sm">
                          {signer.address?.slice(0, 10)}...{signer.address?.slice(-8)}
                        </code>
                      </div>
                      {signer.label && (
                        <span className="text-white/40 text-sm">{signer.label}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Turnkey Signer Address (Gas Funding Notice) */}
          {hasSafe && turnkeyAddress && (
            <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-400">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-yellow-400 font-medium text-sm mb-1">Keep Signer Funded</h4>
                  <p className="text-yellow-400/70 text-sm mb-3">
                    The signer at the address below needs ETH for gas whenever executing Safe transactions. 
                    We recommend maintaining at least <strong>0.002 ETH</strong> on Robinhood Network.
                  </p>
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-black/30">
                    <code className="text-white/80 font-mono text-sm flex-1 truncate">{turnkeyAddress}</code>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(turnkeyAddress);
                      }}
                      className="px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-400 text-xs font-medium hover:bg-yellow-500/20 transition-colors"
                      data-testid="copy-turnkey-address"
                    >
                      Copy
                    </button>
                    <a
                      href="https://bridge.base.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-medium hover:bg-blue-500/20 transition-colors"
                    >
                      Bridge ETH
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs: Queue / Rejected / History */}
          {hasSafe && (
            <div className="border-b border-white/[0.06]">
              <div className="flex gap-1">
                <button
                  onClick={() => setActiveTab('queue')}
                  className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === 'queue' ? 'text-white' : 'text-white/50 hover:text-white/70'
                  }`}
                >
                  Pending
                  {pendingTxs.length > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-[10px] font-bold">
                      {pendingTxs.length}
                    </span>
                  )}
                  {activeTab === 'queue' && (
                    <motion.div layoutId="activeMultiSigTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('rejected')}
                  className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === 'rejected' ? 'text-white' : 'text-white/50 hover:text-white/70'
                  }`}
                >
                  Rejected
                  {rejectedTxs.length > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-[10px] font-bold">
                      {rejectedTxs.length}
                    </span>
                  )}
                  {activeTab === 'rejected' && (
                    <motion.div layoutId="activeMultiSigTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === 'history' ? 'text-white' : 'text-white/50 hover:text-white/70'
                  }`}
                >
                  Executed
                  {activeTab === 'history' && (
                    <motion.div layoutId="activeMultiSigTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Pending Transactions Queue */}
          {hasSafe && activeTab === 'queue' && (
            <div className="space-y-3">
              {/* Propose Transaction Button */}
              <div className="flex justify-end">
                <motion.button
                  onClick={() => setShowProposeModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-black font-medium text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="propose-tx-btn"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Propose Transaction
                </motion.button>
              </div>

              {pendingTxs.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
                    <Icons.Transactions size={24} className="text-white/30" />
                  </div>
                  <p className="text-white/50">No pending transactions</p>
                  <p className="text-white/30 text-sm mt-1">Transactions requiring approval will appear here</p>
                </div>
              ) : (
                pendingTxs.map((tx) => (
                  <SafeTxCard 
                    key={tx.id} 
                    tx={tx} 
                    token={token}
                    safe={safe}
                    turnkeyAddress={turnkeyAddress}
                    onAction={loadSafeData}
                  />
                ))
              )}
            </div>
          )}

          {/* Rejected Transactions */}
          {hasSafe && activeTab === 'rejected' && (
            <div className="space-y-2">
              {rejectedTxs.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
                    <Icons.Transactions size={24} className="text-white/30" />
                  </div>
                  <p className="text-white/50">No rejected transactions</p>
                  <p className="text-white/30 text-sm mt-1">Rejected transactions will appear here</p>
                </div>
              ) : (
                rejectedTxs.map((tx) => (
                  <TxHistoryRow key={tx.id} tx={tx} />
                ))
              )}
            </div>
          )}

          {/* Transaction History */}
          {hasSafe && activeTab === 'history' && (
            <div className="space-y-2">
              {allTxs.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-white/50">No transaction history</p>
                </div>
              ) : (
                allTxs.map((tx) => (
                  <TxHistoryRow key={tx.id} tx={tx} />
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Deploy Safe Modal */}
      <AnimatePresence>
        {showDeployModal && (
          <DeploySafeModal
            agentId={selectedAgent?.id}
            token={token}
            turnkeyAddress={turnkeyAddress}
            onClose={() => setShowDeployModal(false)}
            onSuccess={() => {
              setShowDeployModal(false);
              loadSafeData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Propose Transaction Modal */}
      <AnimatePresence>
        {showProposeModal && (
          <ProposeTransactionModal
            agentId={selectedAgent?.id}
            token={token}
            safeAddress={safe?.address}
            onClose={() => setShowProposeModal(false)}
            onSuccess={() => {
              setShowProposeModal(false);
              loadSafeData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Import Safe Modal */}
      <AnimatePresence>
        {showImportModal && (
          <ImportSafeModal
            agentId={selectedAgent?.id}
            token={token}
            onClose={() => setShowImportModal(false)}
            onSuccess={() => {
              setShowImportModal(false);
              loadSafeData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Discover Safes Modal */}
      <AnimatePresence>
        {showDiscoverModal && (
          <DiscoverSafesModal
            agentId={selectedAgent?.id}
            token={token}
            onClose={() => setShowDiscoverModal(false)}
            onSuccess={() => {
              setShowDiscoverModal(false);
              loadSafeData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Manage Signers Modal */}
      <AnimatePresence>
        {showManageSignersModal && (
          <ManageSignersModal
            agentId={selectedAgent?.id}
            token={token}
            safe={safe}
            onClose={() => setShowManageSignersModal(false)}
            onSuccess={() => {
              setShowManageSignersModal(false);
              loadSafeData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Change Threshold Modal */}
      <AnimatePresence>
        {showChangeThresholdModal && (
          <ChangeThresholdModal
            agentId={selectedAgent?.id}
            token={token}
            safe={safe}
            onClose={() => setShowChangeThresholdModal(false)}
            onSuccess={() => {
              setShowChangeThresholdModal(false);
              loadSafeData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Safe Transaction Card with actions (v8 - signature required for confirm)
function SafeTxCard({ tx, token, safe, turnkeyAddress, onAction }) {
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');

  const confirmations = tx.confirmations?.length || 0;
  const threshold = tx.threshold || 2;
  const progress = (confirmations / threshold) * 100;
  const canExecute = tx.status === 'CONFIRMED' || confirmations >= threshold;
  const status = STATUS_COLORS[tx.status] || STATUS_COLORS.PROPOSED;

  // Confirm transaction - requires wallet signature (v8)
  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    
    try {
      // Check if wallet is connected
      if (!window.ethereum) {
        throw new Error('Please connect your wallet to sign this transaction');
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = provider.getSigner();
      const signerAddress = await signer.getAddress();

      // Check if signer is authorized
      const isAuthorized = safe?.signers?.some(
        s => s.address?.toLowerCase() === signerAddress.toLowerCase()
      );
      if (!isAuthorized) {
        throw new Error('Your connected wallet is not an authorized signer for this Safe');
      }

      // Sign the safeTxHash with eth_sign (raw bytes)
      const safeTxHash = tx.safeTxHash;
      const rawSignature = await signer.signMessage(
        ethers.utils.arrayify(safeTxHash)
      );

      // Adjust v value for Safe format (27→31, 28→32)
      const safeSignature = adjustSignatureForSafe(rawSignature);

      // Submit confirmation to backend
      const res = await api.confirmSafeTx(token, tx.id, signerAddress, safeSignature);
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to confirm');
      }
      
      onAction();
    } catch (err) {
      console.error('Confirm error:', err);
      if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
        setError('Signature rejected. Please sign to confirm the transaction.');
      } else {
        setError(err.message || 'Failed to confirm transaction');
      }
    }
    setConfirming(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    setError('');
    try {
      // Get connected wallet address
      let signerAddress = '';
      if (window.ethereum) {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        signerAddress = await signer.getAddress();
      }
      
      const res = await api.rejectSafeTx(token, tx.id, signerAddress, 'Rejected by user');
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to reject');
      }
      onAction();
    } catch (err) {
      alert('Failed to reject transaction');
    }
    setRejecting(false);
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const res = await api.executeSafeTx(token, tx.id);
      if (!res.success) {
        alert(res.error?.message || 'Failed to execute');
      }
      onAction();
    } catch (err) {
      alert('Failed to execute transaction');
    }
    setExecuting(false);
  };

  return (
    <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/[0.05] flex items-center justify-center">
            <Icons.Transactions size={18} className="text-white/50" />
          </div>
          <div>
            <p className="text-white font-medium capitalize">{tx.plugin || 'Transaction'}</p>
            <p className="text-white/40 text-sm">{tx.description || `To: ${tx.to?.slice(0, 10)}...`}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {tx.value && (
            <span className="text-white/60 text-sm">{(parseFloat(tx.value) / 1e18).toFixed(4)} ETH</span>
          )}
          <span className={`px-2 py-1 rounded text-xs font-medium ${status.bg} ${status.text} ${status.border} border`}>
            {tx.status}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-white/40 mb-2">
          <span>Confirmations</span>
          <span>{confirmations} / {threshold}</span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <motion.div 
            className="h-full rounded-full bg-green-400"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Signers Status */}
      {tx.confirmations && tx.confirmations.length > 0 && (
        <div className="mb-4">
          <p className="text-white/40 text-xs mb-2">Confirmed by:</p>
          <div className="flex flex-wrap gap-2">
            {tx.confirmations.map((conf, i) => (
              <span key={i} className="px-2 py-1 rounded-lg bg-green-500/10 text-green-400 text-xs font-mono">
                {conf.owner?.slice(0, 6)}...{conf.owner?.slice(-4)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
          {/* Show copy address button if error is gas-related and we have turnkey address */}
          {turnkeyAddress && error.toLowerCase().includes('eth') && error.toLowerCase().includes('gas') && (
            <div className="flex items-center gap-2 mt-3 p-2.5 rounded-lg bg-black/30">
              <code className="text-white/80 font-mono text-xs flex-1 truncate">{turnkeyAddress}</code>
              <button 
                onClick={() => navigator.clipboard.writeText(turnkeyAddress)}
                className="px-2.5 py-1 rounded-lg bg-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/30 transition-colors"
              >
                Copy Address
              </button>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        {canExecute ? (
          <motion.button
            onClick={handleExecute}
            disabled={executing}
            className="flex-1 px-4 py-2.5 rounded-xl bg-green-500/20 text-green-400 font-medium text-sm hover:bg-green-500/30 transition-colors disabled:opacity-50"
            whileTap={{ scale: 0.98 }}
            data-testid="execute-tx-btn"
          >
            {executing ? 'Executing...' : 'Execute Transaction'}
          </motion.button>
        ) : (
          <>
            <motion.button
              onClick={handleConfirm}
              disabled={confirming}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-500/20 text-blue-400 font-medium text-sm hover:bg-blue-500/30 transition-colors disabled:opacity-50"
              whileTap={{ scale: 0.98 }}
              data-testid="approve-tx-btn"
            >
              {confirming ? 'Signing...' : 'Sign & Approve'}
            </motion.button>
            <motion.button
              onClick={handleReject}
              disabled={rejecting}
              className="px-4 py-2.5 rounded-xl bg-red-500/10 text-red-400 font-medium text-sm hover:bg-red-500/20 transition-colors disabled:opacity-50"
              whileTap={{ scale: 0.98 }}
              data-testid="reject-tx-btn"
            >
              {rejecting ? '...' : 'Reject'}
            </motion.button>
          </>
        )}
      </div>
    </div>
  );
}

// Transaction History Row
function TxHistoryRow({ tx }) {
  const status = STATUS_COLORS[tx.status] || STATUS_COLORS.PROPOSED;
  const timestamp = tx.executedAt || tx.createdAt;

  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.01] border border-white/[0.04] hover:bg-white/[0.02] transition-colors">
      <div className="flex items-center gap-3">
        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${status.bg} ${status.text}`}>
          {tx.status}
        </span>
        <span className="text-white capitalize">{tx.plugin || 'Transaction'}</span>
        {tx.value && (
          <span className="text-white/40 text-sm">{(parseFloat(tx.value) / 1e18).toFixed(4)} ETH</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        {timestamp && (
          <span className="text-white/30 text-xs">{new Date(timestamp).toLocaleDateString()}</span>
        )}
        {tx.txHash && (
          <a 
            href={getExplorerTxUrl('base', tx.txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/40 hover:text-white/60"
          >
            <Icons.External size={14} />
          </a>
        )}
      </div>
    </div>
  );
}

// Deploy Safe Modal (v8 - with funding notice)
function DeploySafeModal({ agentId, token, turnkeyAddress, onClose, onSuccess }) {
  const [threshold, setThreshold] = useState(2);
  const [signers, setSigners] = useState([
    { address: '', label: 'Me' },
    { address: '', label: '' }
  ]);
  const [chain, setChain] = useState('base');
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState('');

  // Validate agentId is present
  useEffect(() => {
    if (!agentId) {
      setError('Please select an agent first');
    }
  }, [agentId]);

  const addSigner = () => {
    if (signers.length < 5) {
      setSigners([...signers, { address: '', label: '' }]);
    }
  };

  const removeSigner = (index) => {
    if (signers.length > 2) {
      const updated = signers.filter((_, i) => i !== index);
      setSigners(updated);
      if (threshold > updated.length) {
        setThreshold(updated.length);
      }
    }
  };

  const updateSigner = (index, field, value) => {
    const updated = [...signers];
    updated[index][field] = value;
    setSigners(updated);
  };

  const handleDeploy = async () => {
    // Check for agentId first
    if (!agentId) {
      setError('Please select an agent first');
      return;
    }

    const validSigners = signers.filter(s => s.address && s.address.startsWith('0x') && s.address.length === 42);
    if (validSigners.length < 2) {
      setError('At least 2 valid signer addresses required (0x... format, 42 characters)');
      return;
    }
    if (threshold < 1 || threshold > validSigners.length) {
      setError(`Threshold must be between 1 and ${validSigners.length}`);
      return;
    }

    setDeploying(true);
    setError('');
    
    try {
      console.log('Deploying Safe with agentId:', agentId, 'chain:', chain, 'threshold:', threshold, 'signers:', validSigners);
      const res = await api.deploySafe(token, agentId, chain, threshold, validSigners);
      if (res.success) {
        onSuccess();
      } else {
        // Check for gas-related errors in the response
        const errorMessage = res.error?.message || '';
        const errorCode = res.error?.code || '';
        
        if (
          errorMessage.toLowerCase().includes('gas required exceeds allowance') ||
          errorMessage.toLowerCase().includes('insufficient funds') ||
          errorCode === 'INSUFFICIENT_FUNDS' ||
          errorCode === 'INSUFFICIENT_GAS'
        ) {
          const shortAddress = turnkeyAddress 
            ? `${turnkeyAddress.slice(0, 6)}...${turnkeyAddress.slice(-4)}`
            : 'your agent\'s signer wallet';
          setError(`Your agent's signer wallet (${shortAddress}) needs ETH on ${CHAINS[chain]?.name || chain} to pay for gas. Send at least 0.001 ETH to deploy the Safe.`);
        } else {
          // Use the getErrorMessage helper for other error messages
          const errorMsg = getErrorMessage(res.error);
          setError(errorMsg);
        }
      }
    } catch (err) {
      // Check for specific error conditions in caught exceptions
      const errorMessage = err.message?.toLowerCase() || '';
      
      if (
        errorMessage.includes('gas required exceeds allowance') ||
        errorMessage.includes('insufficient funds') ||
        errorMessage.includes('insufficient balance')
      ) {
        const shortAddress = turnkeyAddress 
          ? `${turnkeyAddress.slice(0, 6)}...${turnkeyAddress.slice(-4)}`
          : 'your agent\'s signer wallet';
        setError(`Your agent's signer wallet (${shortAddress}) needs ETH on ${CHAINS[chain]?.name || chain} to pay for gas. Send at least 0.001 ETH to deploy the Safe.`);
      } else if (err.code === 'SAFE_EXISTS') {
        setError('Agent already has a Safe on this chain.');
      } else if (err.code === 'RATE_LIMITED') {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError('Failed to deploy Safe. Please try again.');
      }
    }
    setDeploying(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Deploy Gnosis Safe</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Chain */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Chain</label>
            <select
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              {Object.values(CHAINS).map(c => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Threshold */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Threshold (signatures required)
            </label>
            <select
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value))}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              {[...Array(signers.length)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1} of {signers.length}</option>
              ))}
            </select>
          </div>

          {/* Signers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white/70">Signers</label>
              <button
                onClick={addSigner}
                disabled={signers.length >= 5}
                className="text-xs text-blue-400 hover:text-blue-300 disabled:text-white/30"
              >
                + Add Signer
              </button>
            </div>
            <div className="space-y-3">
              {signers.map((signer, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={signer.address}
                    onChange={(e) => updateSigner(index, 'address', e.target.value)}
                    placeholder="0x..."
                    className="flex-1 px-3 py-2.5 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none focus:border-white/20"
                  />
                  <input
                    type="text"
                    value={signer.label}
                    onChange={(e) => updateSigner(index, 'label', e.target.value)}
                    placeholder="Label"
                    className="w-24 px-3 py-2.5 rounded-xl bg-black border border-white/10 text-white text-sm outline-none focus:border-white/20"
                  />
                  {signers.length > 2 && (
                    <button
                      onClick={() => removeSigner(index)}
                      className="p-2 text-white/30 hover:text-red-400"
                    >
                      <Icons.Close size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Funding Warning (v8 requirement) */}
          <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-400">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-yellow-400 font-medium text-sm mb-1">Fund Your Agent Wallet First</h4>
                <p className="text-yellow-400/70 text-sm mb-2">
                  Your agent's signer wallet needs at least <strong>0.001 ETH on {CHAINS[chain]?.name || chain}</strong> to pay for gas fees. 
                  This covers Safe deployment and future transaction execution.
                </p>
                {turnkeyAddress && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-black/30">
                    <code className="text-white/80 font-mono text-xs flex-1 truncate">{turnkeyAddress}</code>
                    <button 
                      onClick={() => navigator.clipboard.writeText(turnkeyAddress)}
                      className="px-2.5 py-1 rounded-lg bg-yellow-500/10 text-yellow-400 text-xs font-medium hover:bg-yellow-500/20 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              Your agent's Turnkey EOA will be automatically added as a signer. 
              The Safe will be deployed on-chain and ready to use once confirmed.
            </p>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            Cancel
          </button>
          <motion.button
            onClick={handleDeploy}
            disabled={deploying}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {deploying ? 'Deploying...' : 'Deploy Safe'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Propose Transaction Modal (v8)
function ProposeTransactionModal({ agentId, token, safeAddress, onClose, onSuccess }) {
  const [to, setTo] = useState('');
  const [value, setValue] = useState('');
  const [data, setData] = useState('0x');
  const [description, setDescription] = useState('');
  const [proposing, setProposing] = useState(false);
  const [error, setError] = useState('');

  const handlePropose = async () => {
    // Validate
    if (!to || !to.startsWith('0x') || to.length !== 42) {
      setError('Please enter a valid recipient address (0x... format, 42 characters)');
      return;
    }
    if (!value || isNaN(parseFloat(value)) || parseFloat(value) < 0) {
      setError('Please enter a valid ETH amount');
      return;
    }

    setProposing(true);
    setError('');

    try {
      // Convert ETH to wei
      const valueWei = (parseFloat(value) * 1e18).toString();
      
      const res = await api.proposeSafeTx(token, agentId, {
        to,
        value: valueWei,
        data: data || '0x',
        plugin: 'transfer',
        description: description || `Send ${value} ETH to ${to.slice(0, 10)}...`,
      });

      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to propose transaction');
      }
    } catch (err) {
      setError('Failed to propose transaction. Please try again.');
    }
    setProposing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Propose Transaction</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Info */}
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              This will create a Safe transaction that requires confirmation from other signers before execution.
            </p>
          </div>

          {/* Recipient */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Recipient Address</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none focus:border-white/20 transition-colors"
              data-testid="propose-to-input"
            />
          </div>

          {/* Value */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Amount (ETH)</label>
            <input
              type="number"
              step="0.0001"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0.0"
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20 transition-colors"
              data-testid="propose-value-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Send funds to..."
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20 transition-colors"
              data-testid="propose-description-input"
            />
          </div>

          {/* Advanced: Data field (collapsed by default) */}
          <details className="group">
            <summary className="text-white/50 text-sm cursor-pointer hover:text-white/70">
              Advanced: Custom calldata
            </summary>
            <div className="mt-3">
              <input
                type="text"
                value={data}
                onChange={(e) => setData(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none"
              />
            </div>
          </details>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            Cancel
          </button>
          <motion.button
            onClick={handlePropose}
            disabled={proposing}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="submit-propose-btn"
          >
            {proposing ? 'Proposing...' : 'Propose Transaction'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
// Import Safe Modal
function ImportSafeModal({ agentId, token, onClose, onSuccess }) {
  const [safeAddress, setSafeAddress] = useState('');
  const [chain, setChain] = useState('base');
  const [label, setLabel] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const handleImport = async () => {
    if (!safeAddress || !safeAddress.startsWith('0x') || safeAddress.length !== 42) {
      setError('Please enter a valid Safe address (0x... format, 42 characters)');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const res = await api.importSafe(token, { safeAddress, chain, label: label || undefined });
      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to import Safe');
      }
    } catch (err) {
      setError('Failed to import Safe. Please try again.');
    }
    setImporting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Import Existing Safe</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              Import an existing Gnosis Safe that you already own. Your agent's Turnkey EOA must be a signer on this Safe.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Safe Address</label>
            <input
              type="text"
              value={safeAddress}
              onChange={(e) => setSafeAddress(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none focus:border-white/20 transition-colors"
              data-testid="import-safe-address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Chain</label>
            <select
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              {Object.values(CHAINS).map(c => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Treasury Safe"
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white text-sm outline-none focus:border-white/20 transition-colors"
              data-testid="import-safe-label"
            />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            Cancel
          </button>
          <motion.button
            onClick={handleImport}
            disabled={importing}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="submit-import-btn"
          >
            {importing ? 'Importing...' : 'Import Safe'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Discover Safes Modal
function DiscoverSafesModal({ agentId, token, onClose, onSuccess }) {
  const [chain, setChain] = useState('base');
  const [autoImport, setAutoImport] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredSafes, setDiscoveredSafes] = useState([]);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleDiscover = async () => {
    setDiscovering(true);
    setError('');
    setDiscoveredSafes([]);

    try {
      const res = await api.discoverSafes(token, agentId, chain, autoImport);
      if (res.success) {
        setDiscoveredSafes(res.data?.safes || []);
        setSearched(true);
        if (autoImport && res.data?.safes?.length > 0) {
          onSuccess();
        }
      } else {
        setError(res.error?.message || 'Failed to discover Safes');
      }
    } catch (err) {
      setError('Failed to discover Safes. Please try again.');
    }
    setDiscovering(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Discover Safes</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              Automatically find Gnosis Safes where your agent's Turnkey EOA is already a signer.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Chain</label>
            <select
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              {Object.values(CHAINS).map(c => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoImport}
              onChange={(e) => setAutoImport(e.target.checked)}
              className="w-4 h-4 rounded bg-white/10 border-white/20"
            />
            <span className="text-white/70 text-sm">Auto-import discovered Safes</span>
          </label>

          {/* Discovered Safes Results */}
          {searched && (
            <div className="space-y-2">
              {discoveredSafes.length > 0 ? (
                <>
                  <p className="text-white/50 text-xs uppercase tracking-wider">Found {discoveredSafes.length} Safe(s)</p>
                  {discoveredSafes.map((safe, i) => (
                    <div key={i} className="p-3 rounded-xl bg-green-500/5 border border-green-500/10">
                      <code className="text-green-400 font-mono text-sm">
                        {safe.address?.slice(0, 14)}...{safe.address?.slice(-12)}
                      </code>
                      {autoImport && (
                        <span className="ml-2 text-green-400/60 text-xs">✓ Imported</span>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center">
                  <p className="text-white/50 text-sm">No Safes found on {CHAINS[chain]?.name}</p>
                  <p className="text-white/30 text-xs mt-1">Your agent's Turnkey EOA is not a signer on any Safe on this chain.</p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            {searched ? 'Close' : 'Cancel'}
          </button>
          {!searched && (
            <motion.button
              onClick={handleDiscover}
              disabled={discovering}
              className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="submit-discover-btn"
            >
              {discovering ? 'Searching...' : 'Search for Safes'}
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Manage Signers Modal
function ManageSignersModal({ agentId, token, safe, onClose, onSuccess }) {
  const [mode, setMode] = useState('view'); // 'view' | 'add' | 'remove'
  const [newSignerAddress, setNewSignerAddress] = useState('');
  const [newSignerLabel, setNewSignerLabel] = useState('');
  const [newThreshold, setNewThreshold] = useState(safe?.threshold || 2);
  const [selectedRemoveSigner, setSelectedRemoveSigner] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const signerCount = safe?.signers?.length || 0;

  const handleAddSigner = async () => {
    if (!newSignerAddress || !newSignerAddress.startsWith('0x') || newSignerAddress.length !== 42) {
      setError('Please enter a valid signer address (0x... format, 42 characters)');
      return;
    }

    // Check if signer already exists
    if (safe?.signers?.some(s => s.address?.toLowerCase() === newSignerAddress.toLowerCase())) {
      setError('This address is already a signer');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const res = await api.addSafeSigner(token, agentId, {
        address: newSignerAddress,
        label: newSignerLabel || undefined,
        newThreshold: newThreshold
      });
      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to add signer. A transaction will be proposed.');
      }
    } catch (err) {
      setError('Failed to add signer. Please try again.');
    }
    setProcessing(false);
  };

  const handleRemoveSigner = async () => {
    if (!selectedRemoveSigner) {
      setError('Please select a signer to remove');
      return;
    }

    if (signerCount <= 1) {
      setError('Cannot remove the last signer');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const res = await api.removeSafeSigner(token, agentId, selectedRemoveSigner);
      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to remove signer. A transaction will be proposed.');
      }
    } catch (err) {
      setError('Failed to remove signer. Please try again.');
    }
    setProcessing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Manage Signers</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Mode Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => { setMode('view'); setError(''); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'view' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/70'
              }`}
            >
              Current Signers
            </button>
            <button
              onClick={() => { setMode('add'); setError(''); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'add' ? 'bg-green-500/20 text-green-400' : 'text-white/50 hover:text-white/70'
              }`}
            >
              + Add Signer
            </button>
            <button
              onClick={() => { setMode('remove'); setError(''); setSelectedRemoveSigner(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'remove' ? 'bg-red-500/20 text-red-400' : 'text-white/50 hover:text-white/70'
              }`}
            >
              − Remove Signer
            </button>
          </div>

          {/* View Mode */}
          {mode === 'view' && (
            <div className="space-y-2">
              <p className="text-white/40 text-xs uppercase tracking-wider">
                {signerCount} Signer{signerCount !== 1 ? 's' : ''} (Threshold: {safe?.threshold})
              </p>
              {safe?.signers?.map((signer, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-medium">
                      {i + 1}
                    </div>
                    <code className="text-white/70 font-mono text-sm">
                      {signer.address?.slice(0, 10)}...{signer.address?.slice(-8)}
                    </code>
                  </div>
                  {signer.label && (
                    <span className="text-white/40 text-sm">{signer.label}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Mode */}
          {mode === 'add' && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
                <p className="text-yellow-400/80 text-sm">
                  Adding a signer requires a Safe transaction that must be approved by existing signers.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">New Signer Address</label>
                <input
                  type="text"
                  value={newSignerAddress}
                  onChange={(e) => setNewSignerAddress(e.target.value)}
                  placeholder="0x..."
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none focus:border-white/20 transition-colors"
                  data-testid="add-signer-address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Label (optional)</label>
                <input
                  type="text"
                  value={newSignerLabel}
                  onChange={(e) => setNewSignerLabel(e.target.value)}
                  placeholder="e.g. Team Member"
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white text-sm outline-none focus:border-white/20 transition-colors"
                  data-testid="add-signer-label"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  New Threshold (after adding)
                </label>
                <select
                  value={newThreshold}
                  onChange={(e) => setNewThreshold(parseInt(e.target.value))}
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                >
                  {[...Array(signerCount + 1)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} of {signerCount + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Remove Mode */}
          {mode === 'remove' && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/10">
                <p className="text-red-400/80 text-sm">
                  Removing a signer requires a Safe transaction. The threshold may be automatically adjusted.
                </p>
              </div>

              <p className="text-white/40 text-xs uppercase tracking-wider">Select signer to remove</p>
              <div className="space-y-2">
                {safe?.signers?.map((signer, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedRemoveSigner(signer.address)}
                    disabled={signerCount <= 1}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border transition-colors ${
                      selectedRemoveSigner === signer.address
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'
                    } ${signerCount <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        selectedRemoveSigner === signer.address ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/60'
                      }`}>
                        {i + 1}
                      </div>
                      <code className="text-white/70 font-mono text-sm">
                        {signer.address?.slice(0, 10)}...{signer.address?.slice(-8)}
                      </code>
                    </div>
                    {selectedRemoveSigner === signer.address && (
                      <span className="text-red-400 text-sm">Selected</span>
                    )}
                  </button>
                ))}
              </div>
              {signerCount <= 1 && (
                <p className="text-red-400/60 text-xs">Cannot remove the last signer</p>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            Cancel
          </button>
          {mode === 'add' && (
            <motion.button
              onClick={handleAddSigner}
              disabled={processing}
              className="px-5 py-2.5 rounded-xl bg-green-500 text-white font-medium text-sm disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="submit-add-signer-btn"
            >
              {processing ? 'Processing...' : 'Add Signer'}
            </motion.button>
          )}
          {mode === 'remove' && selectedRemoveSigner && (
            <motion.button
              onClick={handleRemoveSigner}
              disabled={processing}
              className="px-5 py-2.5 rounded-xl bg-red-500 text-white font-medium text-sm disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="submit-remove-signer-btn"
            >
              {processing ? 'Processing...' : 'Remove Signer'}
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Change Threshold Modal
function ChangeThresholdModal({ agentId, token, safe, onClose, onSuccess }) {
  const [threshold, setThreshold] = useState(safe?.threshold || 2);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const signerCount = safe?.signers?.length || 0;

  const handleChangeThreshold = async () => {
    if (threshold < 1 || threshold > signerCount) {
      setError(`Threshold must be between 1 and ${signerCount}`);
      return;
    }

    if (threshold === safe?.threshold) {
      setError('New threshold must be different from current threshold');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const res = await api.changeSafeThreshold(token, agentId, threshold);
      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to change threshold. A transaction will be proposed.');
      }
    } catch (err) {
      setError('Failed to change threshold. Please try again.');
    }
    setProcessing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Change Threshold</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
            <p className="text-yellow-400/80 text-sm">
              Changing the threshold requires a Safe transaction that must be approved by current signers.
            </p>
          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center justify-between">
              <span className="text-white/50 text-sm">Current Threshold</span>
              <span className="text-white font-medium">{safe?.threshold} of {signerCount}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">New Threshold</label>
            <select
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value))}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              {[...Array(signerCount)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1} of {signerCount} signers required
                </option>
              ))}
            </select>
            <p className="text-white/30 text-xs mt-2">
              A higher threshold means more security but requires more signers for each transaction.
            </p>
          </div>

          {/* Visual representation */}
          <div className="flex items-center gap-2 justify-center">
            {[...Array(signerCount)].map((_, i) => (
              <div
                key={i}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                  i < threshold
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-white/5 text-white/30 border border-white/10'
                }`}
              >
                {i + 1}
              </div>
            ))}
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            Cancel
          </button>
          <motion.button
            onClick={handleChangeThreshold}
            disabled={processing || threshold === safe?.threshold}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="submit-threshold-btn"
          >
            {processing ? 'Processing...' : 'Change Threshold'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

